import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import Table from '@risksmart-app/components/src/table';
import type { GetIndicatorByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Indicator_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import IndicatorResultModel from 'src/pages/indicators/modals/IndicatorResultModal';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import ActionsButton from '@/components/actions-button';
import { useDeleteIndicatorResults } from '@/hooks/mutations/indicator-result';
import { useGetIndicatorResultsByIndicatorId } from '@/hooks/queries';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import { buildResultActionItems } from './actionItems';
import type { IndicatorResultFields } from './config';
import { useGetCollectionTableProps } from './config';
import {
  DEFAULT_INDICATOR_RESULTS_RANGE,
  describeDateRangeForAnalytics,
  filterResultsByDateRange,
  resolveDateRange,
} from './dateRange';
import type { ChartExportFormat } from './exportChart';
import { exportResultsChart } from './exportChart';
import ResultsChart from './ResultsChart';
import ResultsChartLegacy from './ResultsChartLegacy';
import ResultsDateRangePicker from './ResultsDateRangePicker';

interface Props {
  indicatorType: Indicator_Type_Enum;
  parent: GetIndicatorByIdQuery['indicator'][number];
}

const Tab: FC<Props> = ({ indicatorType, parent }) => {
  useI18NSummaryHelpContent('indicator_results.tabHelp');
  const { t } = useTranslation(['common'], { keyPrefix: 'indicator_results' });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const indicatorId = useGetGuidParam('indicatorId');

  const [isEditOpen, setIsEditOpen] = useState(false);

  const [selectedIndicatorResults, setSelectedIndicatorResults] = useState<
    IndicatorResultFields[]
  >([]);

  const [openIndicatorResultId, setOpenIndicatorResultId] = useState<
    string | undefined
  >();

  const chartEnhancementsEnabled = useBooleanFlagValue(
    'indicator_results_chart_enhancements',
    false
  );
  const amplitude = useAmplitude();
  const chartRef = useRef<HighchartsReactRefObject>(null);

  const [appliedDateRange, setAppliedDateRange] =
    useState<DateRangePickerProps.Value>(DEFAULT_INDICATOR_RESULTS_RANGE);

  const resolvedDateRange = useMemo(
    () => resolveDateRange(appliedDateRange) ?? undefined,
    [appliedDateRange]
  );

  const {
    hasPermission: userCanDeleteIndicatorResult,
    loading: userCanDeleteIndicatorResultLoading,
  } = useHasPermissionQuery('delete:indicator_result', parent);

  const {
    hasPermission: userCanAddIndicatorResult,
    loading: userCanAddIndicatorResultLoading,
  } = useHasPermissionQuery('insert:indicator_result', parent);

  const { hasPermission: userCanExport, loading: userCanExportLoading } =
    useHasPermissionQuery('update:indicator', parent);

  const {
    deleteIndicatorResults: deleteIndicatorResult,
    loading: deleteLoading,
  } = useDeleteIndicatorResults();

  const { data, loading, refetch } = useGetIndicatorResultsByIndicatorId({
    queryArgs: { indicatorId },
  });

  const handleModalOpen = () => {
    setIsEditOpen(true);
  };

  const handleModalClose = (saved: boolean) => {
    setOpenIndicatorResultId(undefined);
    setIsEditOpen(false);
    if (saved) {
      refetch();
    }
  };

  const tableProps = useGetCollectionTableProps(
    parent,
    data,
    (indicatorResult) => {
      setOpenIndicatorResultId(indicatorResult.Id);
      setIsEditOpen(true);
    },
    handleModalOpen
  );

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    asyncAction: async () => {
      await deleteIndicatorResult({
        ids: selectedIndicatorResults.map((result) => result.Id),
      });
      setSelectedIndicatorResults([]);
      setIsDeleteModalVisible(false);

      return true;
    },
  });

  // Export needs a chart with something on it: a numeric indicator with at
  // least one result in the applied window (mirrors the chart's empty state).
  const hasResultsInRange =
    indicatorType === Indicator_Type_Enum.Number &&
    filterResultsByDateRange(
      data?.indicator_result ?? [],
      chartEnhancementsEnabled ? resolvedDateRange : undefined
    ).length > 0;

  const handleExport = (format: ChartExportFormat) => {
    amplitude.logEvent('Indicator Results Export Selected', {
      indicator_id: indicatorId,
      format,
    });
    const exported = exportResultsChart(
      chartRef.current?.chart,
      parent.Title,
      format
    );
    if (exported) {
      amplitude.logEvent('Indicator Results Chart Exported', {
        indicator_id: indicatorId,
        date_range: describeDateRangeForAnalytics(appliedDateRange),
        format,
      });
    }
  };

  const actionItems = buildResultActionItems({
    t,
    canAddResult:
      userCanAddIndicatorResult && !userCanAddIndicatorResultLoading,
    canDeleteResult:
      userCanDeleteIndicatorResult && !userCanDeleteIndicatorResultLoading,
    canExport: userCanExport && !userCanExportLoading,
    hasSelectedResults: selectedIndicatorResults.length > 0,
    hasResultsInRange,
    onAddResult: () => {
      amplitude.logEvent('Indicator Result Add Selected', {
        indicator_id: indicatorId,
      });
      handleModalOpen();
    },
    onDeleteResult: () => {
      amplitude.logEvent('Indicator Result Delete Selected', {
        indicator_id: indicatorId,
      });
      setIsDeleteModalVisible(true);
    },
    onExport: handleExport,
  });

  return (
    <>
      <Table
        {...tableProps}
        selectionType={
          userCanDeleteIndicatorResult && !userCanDeleteIndicatorResultLoading
            ? 'multi'
            : undefined
        }
        selectedItems={selectedIndicatorResults}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedIndicatorResults(detail.selectedItems);
        }}
        resizableColumns={true}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              variant={'h2'}
              actions={
                <SpaceBetween
                  direction={'horizontal'}
                  size={'xs'}
                  alignItems={'center'}
                >
                  {chartEnhancementsEnabled &&
                    indicatorType === Indicator_Type_Enum.Number && (
                      <ResultsDateRangePicker
                        value={appliedDateRange}
                        onChange={setAppliedDateRange}
                      />
                    )}
                  {chartEnhancementsEnabled ? (
                    actionItems.length > 0 ? (
                      <ActionsButton
                        buttonText={t('actions.actions_button')}
                        testId={'indicatorResultActions'}
                        variant={'primary'}
                        items={actionItems}
                        onOpen={() => {
                          amplitude.logEvent(
                            'Indicator Results Actions Opened',
                            { indicator_id: indicatorId }
                          );
                        }}
                        onDisabledItemHover={(itemId) => {
                          amplitude.logEvent(
                            'Indicator Results Disabled Action Hovered',
                            { indicator_id: indicatorId, item: itemId }
                          );
                        }}
                      />
                    ) : null
                  ) : (
                    <SpaceBetween direction={'horizontal'} size={'xs'}>
                      <Permission
                        permission={'delete:indicator_result'}
                        parentObject={parent}
                      >
                        <Button
                          formAction={'none'}
                          variant={'normal'}
                          disabled={
                            !selectedIndicatorResults.length ||
                            !userCanDeleteIndicatorResult ||
                            userCanDeleteIndicatorResultLoading
                          }
                          onClick={() => setIsDeleteModalVisible(true)}
                        >
                          {t('delete_button')}
                        </Button>
                      </Permission>
                      <Permission
                        permission={'insert:indicator_result'}
                        parentObject={parent}
                      >
                        <Button
                          variant={'primary'}
                          formAction={'none'}
                          onClick={handleModalOpen}
                        >
                          {t('create_new_button')}
                        </Button>
                      </Permission>
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              }
            >
              {t('tab_title')}
            </TabHeader>

            {indicatorType === Indicator_Type_Enum.Number &&
              // The Highcharts chart (plus date-range picker and Actions
              // dropdown export) is the enhancement set gated behind
              // `indicator_results_chart_enhancements`. With the flag off, keep
              // serving the legacy Cloudscape chart — the migration reached
              // production ungated and dropped the threshold labels/tooltip
              // context that chart still shows (CUS-227).
              (chartEnhancementsEnabled ? (
                <ResultsChart
                  ref={chartRef}
                  data={data?.indicator_result || []}
                  dateRange={resolvedDateRange}
                  upperAppetite={parent.UpperAppetiteNum}
                  lowerAppetite={parent.LowerAppetiteNum}
                  upperTolerance={parent.UpperToleranceNum}
                  lowerTolerance={parent.LowerToleranceNum}
                />
              ) : (
                <ResultsChartLegacy
                  data={data?.indicator_result || []}
                  upperAppetite={parent.UpperAppetiteNum}
                  lowerAppetite={parent.LowerAppetiteNum}
                  upperTolerance={parent.UpperToleranceNum}
                  lowerTolerance={parent.LowerToleranceNum}
                />
              ))}
          </SpaceBetween>
        }
        variant={'embedded'}
        loading={loading}
        loadingText={t('loading_message') ?? ''}
        sortingDisabled={false}
      />
      {isEditOpen && indicatorId && (
        <IndicatorResultModel
          Id={openIndicatorResultId}
          onDismiss={handleModalClose}
          parentIndicatorId={indicatorId}
          parentIndicatorType={indicatorType}
          indicator={parent}
          parentIndicator={parent}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => {
          refetch();
          setOpenIndicatorResultId(undefined);
          setSelectedIndicatorResults([]);
          setIsDeleteModalVisible(false);
        }}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default Tab;
